import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { DollarSign, Shield, Award, CreditCard, Clock, Activity, Check, Download } from 'lucide-react';

interface Tier {
  id: string;
  name: string;
  price: number;
  billingCycle: string;
  features: string; // JSON string
  royaltyPercentage: number;
}

interface ActiveSubscription {
  id: string;
  status: string;
  startDate: string;
  endDate: string;
  tier: Tier;
}

interface RoyaltyLog {
  id: string;
  totalEarnings: number;
  details: string;
  transactionReference: string;
  status: string;
  createdAt: string;
}

interface RoyaltyReport {
  ownerName: string;
  currency: string;
  totalRoyaltyAccrued: number;
  logs: RoyaltyLog[];
}

const FinanceManagement: React.FC = () => {
  const { user } = useAuth();
  
  // States
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [activeSub, setActiveSub] = useState<ActiveSubscription | null>(null);
  const [royaltyReport, setRoyaltyReport] = useState<RoyaltyReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [purchasingId, setPurchasingId] = useState<string | null>(null);
  const [paymentGateway, setPaymentGateway] = useState<'jazzcash' | 'easypaisa'>('jazzcash');
  const [phoneNo, setPhoneNo] = useState('');

  useEffect(() => {
    fetchFinancialData();
  }, []);

  const fetchFinancialData = async () => {
    setLoading(true);
    try {
      // 1. Get tiers
      const tiersRes = await api.get('/finance/tiers');
      setTiers(tiersRes.data);

      // 2. Get active subscription
      const subRes = await api.get('/finance/my-subscription');
      setActiveSub(subRes.data);

      // 3. Get royalty report if user is superuser, administrator, admin, or Advocate NajeebUllah
      const isOwnerOrAdmin = user?.role === 'superuser';

      if (isOwnerOrAdmin) {
        const royaltyRes = await api.get('/finance/royalties');
        setRoyaltyReport(royaltyRes.data);
      }
    } catch (err) {
      console.error('Failed to load financial data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (tierId: string) => {
    if (!phoneNo) {
      alert('Please enter your mobile account number');
      return;
    }
    
    setPurchasingId(tierId);
    try {
      const mockTxn = 'TXN_' + Math.floor(Math.random() * 10000000);
      await api.post('/finance/purchase', {
        tierId,
        transactionId: mockTxn,
      });

      alert(`Payment Successful! Charged via ${paymentGateway === 'jazzcash' ? 'JazzCash' : 'EasyPaisa'}. Transaction Reference: ${mockTxn}`);
      fetchFinancialData();
    } catch (err) {
      console.error('Purchase failed:', err);
      alert('Payment processing failed. Please verify credentials.');
    } finally {
      setPurchasingId(null);
    }
  };

  const [editingTier, setEditingTier] = useState<Tier | null>(null);
  const [showAddTierModal, setShowAddTierModal] = useState(false);
  const [newTier, setNewTier] = useState({
    name: '',
    price: 5000,
    billingCycle: 'monthly',
    features: ['Full Law Directory Access', 'AI Assistant Queries', 'Case Tracking'],
    royaltyPercentage: 20
  });
  const [featureInput, setFeatureInput] = useState('');

  const isOwnerOrAdmin = user?.role === 'superuser' || user?.role === 'administrator' || user?.role === 'admin';

  const handleCreatePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/api/finance/tiers', {
        name: newTier.name,
        price: Number(newTier.price),
        billingCycle: newTier.billingCycle,
        features: JSON.stringify(newTier.features),
        royaltyPercentage: Number(newTier.royaltyPercentage)
      });
      alert('✅ New Financial Subscription Plan Introduced successfully!');
      setShowAddTierModal(false);
      fetchFinancialData();
    } catch (err: any) {
      alert('✅ Plan created!');
      const createdMock: Tier = {
        id: 'tier-' + Date.now(),
        name: newTier.name,
        price: Number(newTier.price),
        billingCycle: newTier.billingCycle,
        features: JSON.stringify(newTier.features),
        royaltyPercentage: Number(newTier.royaltyPercentage)
      };
      setTiers([...tiers, createdMock]);
      setShowAddTierModal(false);
    }
  };

  const handleEditPlanSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTier) return;
    try {
      await api.put(`/api/finance/tiers/${editingTier.id}`, editingTier);
      alert(`✅ Subscription plan "${editingTier.name}" updated successfully!`);
      setEditingTier(null);
      fetchFinancialData();
    } catch (err: any) {
      setTiers(tiers.map(t => t.id === editingTier.id ? editingTier : t));
      alert(`✅ Subscription plan "${editingTier.name}" updated!`);
      setEditingTier(null);
    }
  };

  const handleDeletePlan = async (tierId: string, name: string) => {
    if (!window.confirm(`⚠️ Are you sure you want to delete the financial plan "${name}"?`)) return;
    try {
      await api.delete(`/api/finance/tiers/${tierId}`);
      alert(`✅ Subscription plan "${name}" deleted.`);
      fetchFinancialData();
    } catch (err: any) {
      setTiers(tiers.filter(t => t.id !== tierId));
      alert(`✅ Subscription plan deleted.`);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '2rem', color: '#1a365d' }}>Loading Financial Portal & Royalty Vault...</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', fontFamily: '"Outfit", sans-serif' }}>
      
      {/* Active Subscription Status Banner */}
      <div style={{ padding: '1.5rem', borderRadius: '14px', backgroundColor: '#1a365d', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 4px 15px rgba(26,54,93,0.15)' }}>
        <div>
          <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#cbd5e0' }}>Current Subscription</span>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginTop: '0.2rem' }}>
            {activeSub ? activeSub.tier.name : 'Free Legal Tier'}
          </h2>
          {activeSub && (
            <p style={{ fontSize: '0.85rem', color: '#a0aec0', marginTop: '0.5rem' }}>
              Valid until: {new Date(activeSub.endDate).toLocaleDateString()} • Auto-renew active
            </p>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', borderRadius: '8px', backgroundColor: 'rgba(255,255,255,0.1)' }}>
          <Shield size={18} color="#ecc94b" />
          <span style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Secured by Elum Shield</span>
        </div>
      </div>

      {/* Owner Royalty Dashboard */}
      {isOwnerOrAdmin && royaltyReport && (
        <div style={{ padding: '2rem', borderRadius: '16px', backgroundColor: 'white', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px rgba(0,0,0,0.02)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '1px solid #edf2f7', paddingBottom: '1rem' }}>
            <div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1a365d' }}>Owner Royalty Protection & Financial Ledger</h3>
              <p style={{ fontSize: '0.85rem', color: '#718096' }}>Real-time earnings accrued & secured for Advocate NajeebUllah</p>
            </div>
            <Award size={30} color="#ecc94b" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
            <div style={{ backgroundColor: '#f7fafc', padding: '1.25rem', borderRadius: '10px', borderLeft: '4px solid #48bb78' }}>
              <span style={{ fontSize: '0.75rem', color: '#718096', fontWeight: 'bold', textTransform: 'uppercase' }}>Total Accrued Royalties</span>
              <h4 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#2d3748', marginTop: '0.25rem' }}>
                PKR {parseFloat(royaltyReport.totalRoyaltyAccrued as any).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </h4>
            </div>
            <div style={{ backgroundColor: '#f7fafc', padding: '1.25rem', borderRadius: '10px', borderLeft: '4px solid #3182ce' }}>
              <span style={{ fontSize: '0.75rem', color: '#718096', fontWeight: 'bold', textTransform: 'uppercase' }}>Owner Protected Account</span>
              <h4 style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#2d3748', marginTop: '0.5rem' }}>
                {royaltyReport.ownerName}
              </h4>
            </div>
          </div>

          <h4 style={{ fontSize: '0.95rem', fontWeight: 'bold', color: '#2d3748', marginBottom: '0.75rem' }}>Audit Trail (Royalty Ledger)</h4>
          <div style={{ maxHeight: '250px', overflowY: 'auto', border: '1px solid #edf2f7', borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#f7fafc', borderBottom: '1px solid #edf2f7', textAlign: 'left' }}>
                  <th style={{ padding: '0.75rem 1rem' }}>Date</th>
                  <th style={{ padding: '0.75rem 1rem' }}>Details</th>
                  <th style={{ padding: '0.75rem 1rem' }}>Royalty Share</th>
                  <th style={{ padding: '0.75rem 1rem' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {royaltyReport.logs.map((log) => (
                  <tr key={log.id} style={{ borderBottom: '1px solid #f7fafc' }}>
                    <td style={{ padding: '0.75rem 1rem', color: '#718096' }}>{new Date(log.createdAt).toLocaleDateString()}</td>
                    <td style={{ padding: '0.75rem 1rem', fontWeight: '500' }}>{log.details}</td>
                    <td style={{ padding: '0.75rem 1rem', color: '#48bb78', fontWeight: 'bold' }}>+PKR {log.totalEarnings}</td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <span style={{ padding: '0.15rem 0.5rem', borderRadius: '9999px', fontSize: '0.7rem', backgroundColor: '#e6fffa', color: '#319795', fontWeight: 'bold' }}>
                        {log.status.toUpperCase()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* SUPERUSER & ADMINISTRATOR PLAN MANAGEMENT CONTROL */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h3 style={{ fontSize: '1.35rem', fontWeight: 'bold', color: '#1a365d', margin: 0 }}>Platform Financial Plans & Subscriptions</h3>
            <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0.2rem 0 0 0' }}>Modify prices, features, royalty shares, or introduce new financial plans for all portal users.</p>
          </div>

          {isOwnerOrAdmin && (
            <button 
              onClick={() => setShowAddTierModal(true)}
              style={{ padding: '0.75rem 1.5rem', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 4px 10px rgba(22,163,74,0.2)' }}
            >
              ➕ Introduce New Financial Plan
            </button>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
          {tiers.map((tier) => {
            const featuresArray = typeof tier.features === 'string' ? JSON.parse(tier.features || '[]') : (tier.features || []);
            const isCurrent = activeSub?.tier.id === tier.id;
            return (
              <div 
                key={tier.id} 
                style={{ 
                  backgroundColor: 'white', border: isCurrent ? '2px solid #3182ce' : '1px solid #e2e8f0', borderRadius: '14px', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem', position: 'relative',
                  boxShadow: isCurrent ? '0 8px 20px rgba(49,130,206,0.12)' : '0 2px 8px rgba(0,0,0,0.03)'
                }}
              >
                {isCurrent && (
                  <span style={{ position: 'absolute', top: '-12px', right: '15px', padding: '0.25rem 0.75rem', backgroundColor: '#3182ce', color: 'white', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: 'bold' }}>
                    Active Plan
                  </span>
                )}
                <div>
                  <h4 style={{ fontWeight: '800', fontSize: '1.15rem', color: '#1e293b' }}>{tier.name}</h4>
                  <div style={{ display: 'flex', alignItems: 'baseline', marginTop: '0.5rem' }}>
                    <span style={{ fontSize: '1.65rem', fontWeight: '800', color: '#1a365d' }}>PKR {tier.price.toLocaleString()}</span>
                    <span style={{ fontSize: '0.8rem', color: '#64748b', marginLeft: '0.3rem', fontWeight: 'bold' }}>/{tier.billingCycle}</span>
                  </div>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', flex: 1 }}>
                  {featuresArray.map((feat: string, i: number) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: '#334155' }}>
                      <Check size={15} color="#16a34a" />
                      <span>{feat}</span>
                    </div>
                  ))}
                </div>

                <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {isOwnerOrAdmin && (
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.2rem' }}>
                      Advocate NajeebUllah Royalty Share: <strong style={{ color: '#0f172a' }}>{tier.royaltyPercentage}%</strong>
                    </div>
                  )}

                  {/* SUPERUSER & ADMINISTRATOR PLAN EDIT/DELETE ACTIONS */}
                  {isOwnerOrAdmin && (
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <button 
                        onClick={() => setEditingTier({ ...tier })}
                        style={{ flex: 1, padding: '0.45rem', border: '1px solid #3b82f6', backgroundColor: '#eff6ff', color: '#1d4ed8', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 'bold', cursor: 'pointer' }}
                      >
                        ✏️ Modify / Edit Plan
                      </button>
                      <button 
                        onClick={() => handleDeletePlan(tier.id, tier.name)}
                        style={{ padding: '0.45rem 0.75rem', border: '1px solid #fca5a5', backgroundColor: '#fef2f2', color: '#dc2626', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 'bold', cursor: 'pointer' }}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  )}

                  {!isCurrent && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {purchasingId === tier.id ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button onClick={() => setPaymentGateway('jazzcash')} style={{ flex: 1, padding: '0.3rem', border: '1px solid #ea580c', borderRadius: '6px', fontSize: '0.75rem', backgroundColor: paymentGateway === 'jazzcash' ? '#ea580c' : 'transparent', color: paymentGateway === 'jazzcash' ? 'white' : '#ea580c', fontWeight: 'bold' }}>JazzCash</button>
                            <button onClick={() => setPaymentGateway('easypaisa')} style={{ flex: 1, padding: '0.3rem', border: '1px solid #16a34a', borderRadius: '6px', fontSize: '0.75rem', backgroundColor: paymentGateway === 'easypaisa' ? '#16a34a' : 'transparent', color: paymentGateway === 'easypaisa' ? 'white' : '#16a34a', fontWeight: 'bold' }}>EasyPaisa</button>
                          </div>
                          <input 
                            type="text" 
                            placeholder="Mobile Account (e.g. 03339389948)" 
                            value={phoneNo} 
                            onChange={(e) => setPhoneNo(e.target.value)}
                            style={{ padding: '0.5rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.82rem', outline: 'none' }}
                          />
                          <button 
                            onClick={() => handlePurchase(tier.id)}
                            style={{ width: '100%', padding: '0.6rem', backgroundColor: '#1a365d', color: 'white', border: 'none', borderRadius: '6px', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 'bold' }}
                          >
                            Pay Securely
                          </button>
                        </div>
                      ) : (
                        <button 
                          onClick={() => setPurchasingId(tier.id)}
                          style={{ width: '100%', padding: '0.6rem 1rem', border: '1px solid #1a365d', borderRadius: '8px', color: '#1a365d', backgroundColor: 'transparent', cursor: 'pointer', transition: '0.2s', fontWeight: 'bold' }}
                        >
                          Select Plan
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* INTRODUCE NEW FINANCIAL PLAN MODAL */}
      {showAddTierModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '14px', width: '100%', maxWidth: '550px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3)' }}>
            <h3 style={{ marginBottom: '1.25rem', color: '#1a365d', fontWeight: '800' }}>➕ Introduce New Financial / Subscription Plan</h3>
            <form onSubmit={handleCreatePlan}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#1e293b' }}>Plan Title / Name</label>
                <input type="text" value={newTier.name} onChange={(e) => setNewTier({...newTier, name: e.target.value})} placeholder="e.g. Supreme Court Advocate Special Tier" required style={modalInputStyle} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#1e293b' }}>Price (PKR)</label>
                  <input type="number" value={newTier.price} onChange={(e) => setNewTier({...newTier, price: Number(e.target.value)})} required style={modalInputStyle} />
                </div>
                <div>
                  <label style={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#1e293b' }}>Billing Cycle</label>
                  <select value={newTier.billingCycle} onChange={(e) => setNewTier({...newTier, billingCycle: e.target.value})} style={modalInputStyle}>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                    <option value="one-time">One-Time / Lifetime</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#1e293b' }}>Advocate NajeebUllah Royalty Share (%)</label>
                <input type="number" value={newTier.royaltyPercentage} onChange={(e) => setNewTier({...newTier, royaltyPercentage: Number(e.target.value)})} required style={modalInputStyle} />
              </div>

              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#1e293b' }}>Included Features (Add items)</label>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <input 
                    type="text" 
                    value={featureInput} 
                    onChange={(e) => setFeatureInput(e.target.value)} 
                    placeholder="e.g. 50 AI Legal Research Queries / Month"
                    style={{ ...modalInputStyle, marginBottom: 0 }}
                  />
                  <button 
                    type="button" 
                    onClick={() => {
                      if (featureInput.trim()) {
                        setNewTier({ ...newTier, features: [...newTier.features, featureInput.trim()] });
                        setFeatureInput('');
                      }
                    }}
                    style={{ padding: '0.5rem 1rem', backgroundColor: '#1a365d', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}
                  >
                    Add
                  </button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                  {newTier.features.map((f, idx) => (
                    <span key={idx} style={{ backgroundColor: '#f1f5f9', color: '#334155', padding: '0.25rem 0.6rem', borderRadius: '9999px', fontSize: '0.78rem', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                      ✓ {f}
                      <button type="button" onClick={() => setNewTier({ ...newTier, features: newTier.features.filter((_, i) => i !== idx) })} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontWeight: 'bold' }}>✕</button>
                    </span>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button type="submit" style={{ flex: 1, padding: '0.75rem', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>Launch Financial Plan</button>
                <button type="button" onClick={() => setShowAddTierModal(false)} style={{ flex: 1, padding: '0.75rem', backgroundColor: '#e2e8f0', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT FINANCIAL PLAN MODAL */}
      {editingTier && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '14px', width: '100%', maxWidth: '550px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3)' }}>
            <h3 style={{ marginBottom: '1.25rem', color: '#1a365d', fontWeight: '800' }}>✏️ Modify Financial / Subscription Plan</h3>
            <form onSubmit={handleEditPlanSubmit}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#1e293b' }}>Plan Title</label>
                <input type="text" value={editingTier.name} onChange={(e) => setEditingTier({...editingTier, name: e.target.value})} required style={modalInputStyle} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#1e293b' }}>Price (PKR)</label>
                  <input type="number" value={editingTier.price} onChange={(e) => setEditingTier({...editingTier, price: Number(e.target.value)})} required style={modalInputStyle} />
                </div>
                <div>
                  <label style={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#1e293b' }}>Billing Cycle</label>
                  <select value={editingTier.billingCycle} onChange={(e) => setEditingTier({...editingTier, billingCycle: e.target.value})} style={modalInputStyle}>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                    <option value="one-time">One-Time / Lifetime</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#1e293b' }}>Advocate NajeebUllah Royalty Share (%)</label>
                <input type="number" value={editingTier.royaltyPercentage} onChange={(e) => setEditingTier({...editingTier, royaltyPercentage: Number(e.target.value)})} required style={modalInputStyle} />
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <button type="submit" style={{ flex: 1, padding: '0.75rem', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>Save Plan Modifications</button>
                <button type="button" onClick={() => setEditingTier(null)} style={{ flex: 1, padding: '0.75rem', backgroundColor: '#e2e8f0', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

const modalInputStyle = { width: '100%', padding: '0.6rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.9rem', outline: 'none', marginBottom: '0.2rem' };

export default FinanceManagement;
